import { ENDPOINTS } from 'hammock';
import { ChangeEvent } from 'react';

import { transformStringToIdentifier } from 'commons/utils';

const getProgress = (setProgress?: (progress: number) => void) => ({
  loaded,
  total,
}: ProgressEvent) => {
  if (setProgress) {
    const progress = Math.floor((loaded / total) * 100);
    setProgress(progress);
  }
};

export interface Props {
  src: string;
}

export interface Loading {
  status: boolean;
  name: string;
  size?: number;
  error?: string;
}

export const uploadImage = (
  setProps: (props: Props) => void,
  setLoading: (loading: Loading) => void,
  setProgress?: (progress: number) => void,
) => (fileList: File[] | null) => {
  const file = fileList ? fileList[0] : undefined;

  setProps({ src: '' });
  if (!file || !file.name) {
    return;
  }

  // append timestamp to filename
  const fileNameArr = file.name.split('.');
  fileNameArr[fileNameArr.length - 2] = `${fileNameArr[fileNameArr.length - 2]}_${Date.now()}`;
  const filenameWithTimestamp = fileNameArr.join('.');

  const formData = new FormData();
  formData.append('media', file);
  formData.append('slug', transformStringToIdentifier(filenameWithTimestamp));

  setLoading({ status: true, name: file.name, size: file.size });
  setProgress?.(0);
  const xhr = new XMLHttpRequest();
  xhr.open('POST', ENDPOINTS.mediaUpload);
  xhr.upload.addEventListener('progress', getProgress(setProgress), false);

  xhr.onloadend = (event: any) => {
    try {
      if (event?.target?.status === 413 || event?.target?.status === '413') {
        setLoading({
          status: false,
          name: file.name,
          size: file.size,
          error:
            'Images must be below 10MB in size and less than 50,000 megapixels (equivalent of 223,606px by 223,606px).',
        });
        return;
      }

      const data = JSON.parse(event.target.response);

      if (data.errorCode as string) {
        setLoading({ status: false, name: file.name, size: file.size, error: data.message });
        return;
      }

      setTimeout(() => {
        setLoading({ status: false, name: file.name, size: file.size });
        setProps({
          src: data.assetUri.uri,
        });
      }, 1500);
    } catch (e) {
      setLoading({ status: false, name: file.name, error: 'Try another file.' });
    }
  };
  xhr.send(formData);
};

export const uploadImageFromInput = (
  setProps: (props: Props) => void,
  setLoading: (loading: Loading) => void,
  setProgress: (progress: number) => void,
) => {
  return (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    return uploadImage(setProps, setLoading, setProgress)(files ? Array.from(files) : null);
  };
};
